import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Plus, GripVertical, X, Save, Share2, Music, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PlaylistEditPage = () => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [songs, setSongs] = useState([]);
  const [allSongs, setAllSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/playlists/${playlistId}`).then(r => { if (!r.ok) throw new Error(); return r.json(); }),
      fetch(`${BACKEND_URL}/api/songs?language=English`).then(r => r.json()).then(d => d.songs || d)
    ]).then(([pl, allS]) => {
      setPlaylist(pl);
      setSongs(pl.songs || []);
      setAllSongs(allS);
    }).catch(() => navigate('/playlists')).finally(() => setLoading(false));
  }, [playlistId, navigate]);

  const addSong = useCallback((song) => {
    if (songs.find(s => s.id === song.id)) return;
    setSongs(prev => [...prev, song]);
    setSaved(false);
  }, [songs]);

  const removeSong = useCallback((id) => {
    setSongs(prev => prev.filter(s => s.id !== id));
    setSaved(false);
  }, []);

  const moveSong = useCallback((fromIdx, toIdx) => {
    setSongs(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIdx, 1);
      updated.splice(toIdx, 0, moved);
      return updated;
    });
    setSaved(false);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/playlists/${playlistId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: playlist.name,
          service_date: playlist.service_date,
          description: playlist.description,
          song_ids: songs.map(s => s.id)
        })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleShare = () => {
    const url = `${window.location.origin}/playlists/${playlistId}`;
    navigator.clipboard.writeText(url);
    alert('Share link copied to clipboard!');
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    moveSong(dragIdx, idx);
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  // Touch drag support
  const [touchStart, setTouchStart] = useState(null);
  const handleTouchStart = (idx, e) => {
    setTouchStart(idx);
  };

  const searchResults = searchTerm.length >= 2
    ? allSongs.filter(s => {
        const q = searchTerm.toLowerCase();
        return s.title.toLowerCase().includes(q) || (s.author && s.author.toLowerCase().includes(q));
      }).slice(0, 15)
    : [];

  if (loading || !playlist) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center pt-20">
        <div className="w-12 h-12 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-16" data-testid="playlist-edit-page">
      {/* Sticky Toolbar */}
      <div className="bg-white border-b border-stone-200 sticky top-16 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button data-testid="back-to-playlists-btn" onClick={() => navigate('/playlists')} className="flex items-center gap-1.5 text-stone-600 hover:text-amber-600 font-medium text-sm">
            <ArrowLeft className="w-4 h-4" /> Playlists
          </button>
          <div className="flex items-center gap-2">
            <button data-testid="share-playlist-btn" onClick={handleShare} className="flex items-center gap-1.5 px-3 py-2 text-stone-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg text-sm font-medium transition-all">
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button data-testid="save-playlist-btn" onClick={handleSave} disabled={saving} className={`flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${saved ? 'bg-emerald-600 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white'} disabled:opacity-50`}>
              {saved ? <><Check className="w-4 h-4" /> Saved</> : saving ? 'Saving...' : <><Save className="w-4 h-4" /> Save</>}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Playlist Header */}
        <div className="mb-6">
          <input
            data-testid="edit-playlist-name"
            type="text"
            value={playlist.name}
            onChange={e => { setPlaylist(p => ({ ...p, name: e.target.value })); setSaved(false); }}
            className="text-2xl sm:text-3xl font-bold text-stone-900 bg-transparent border-none outline-none w-full focus:ring-0 placeholder:text-stone-300"
            placeholder="Playlist name"
          />
          <div className="flex items-center gap-4 mt-2">
            <input
              data-testid="edit-playlist-date"
              type="date"
              value={playlist.service_date || ''}
              onChange={e => { setPlaylist(p => ({ ...p, service_date: e.target.value })); setSaved(false); }}
              className="text-sm text-stone-500 bg-transparent border border-stone-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-amber-500 focus:outline-none"
            />
            <span className="text-sm text-stone-400">{songs.length} songs</span>
          </div>
        </div>

        {/* Song List */}
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden mb-4" data-testid="playlist-songs">
          {songs.length === 0 ? (
            <div className="p-8 text-center" data-testid="playlist-empty">
              <Music className="w-12 h-12 text-stone-300 mx-auto mb-3" />
              <p className="text-stone-500 font-medium mb-1">No songs yet</p>
              <p className="text-stone-400 text-sm">Search and add songs below to build your setlist</p>
            </div>
          ) : (
            <div>
              {songs.map((song, idx) => (
                <div
                  key={song.id}
                  data-testid={`playlist-song-${idx}`}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={e => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={e => handleTouchStart(idx, e)}
                  className={`flex items-center gap-3 px-4 py-3 border-b border-stone-100 last:border-b-0 transition-all ${dragIdx === idx ? 'bg-amber-50 opacity-70' : 'hover:bg-stone-50'}`}
                >
                  <div className="cursor-grab text-stone-300 hover:text-stone-500 touch-none" data-testid={`drag-handle-${idx}`}>
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <span className="flex-shrink-0 w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center text-stone-500 font-bold text-sm">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0" onClick={() => navigate(`/songs/english/${song.id}`)}>
                    <h4 className="font-semibold text-stone-800 truncate cursor-pointer hover:text-amber-700">{song.title}</h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      {song.author && <span className="text-xs text-stone-400 truncate max-w-[180px]">{song.author}</span>}
                      {song.key_signature && <span className="text-xs text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded font-medium">{song.key_signature}</span>}
                    </div>
                  </div>
                  <button
                    data-testid={`remove-song-${idx}`}
                    onClick={() => removeSong(song.id)}
                    className="p-1.5 text-stone-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add Songs Section */}
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4" data-testid="add-songs-section">
          <button
            data-testid="toggle-song-search"
            onClick={() => setShowSearch(!showSearch)}
            className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-semibold text-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> {showSearch ? 'Close Search' : 'Add Songs'}
          </button>

          {showSearch && (
            <div className="mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <input
                  data-testid="song-search-input"
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search songs by title or songwriter..."
                  className="w-full pl-10 pr-4 py-3 bg-stone-50 border border-stone-200 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  autoFocus
                />
              </div>

              {searchResults.length > 0 && (
                <div className="mt-3 border border-stone-200 rounded-lg max-h-72 overflow-y-auto" data-testid="song-search-results">
                  {searchResults.map(song => {
                    const alreadyAdded = songs.find(s => s.id === song.id);
                    return (
                      <button
                        key={song.id}
                        data-testid={`add-song-${song.id}`}
                        onClick={() => !alreadyAdded && addSong(song)}
                        disabled={alreadyAdded}
                        className={`w-full text-left px-4 py-3 border-b border-stone-50 last:border-b-0 flex items-center gap-3 transition-all ${alreadyAdded ? 'opacity-50 bg-stone-50' : 'hover:bg-amber-50'}`}
                      >
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-stone-800 block truncate">{song.title}</span>
                          <div className="flex items-center gap-2 mt-0.5">
                            {song.author && <span className="text-xs text-stone-400">{song.author}</span>}
                            {song.key_signature && <span className="text-xs text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">{song.key_signature}</span>}
                          </div>
                        </div>
                        {alreadyAdded ? (
                          <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded">Added</span>
                        ) : (
                          <Plus className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {searchTerm.length >= 2 && searchResults.length === 0 && (
                <p className="mt-3 text-sm text-stone-400 text-center py-4">No songs match "{searchTerm}"</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaylistEditPage;
